import { Accordion } from "primereact/accordion";
import { ReactNode } from "react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Dialog } from "primereact/dialog";
import { Tag } from "primereact/tag";
import { Steps } from "primereact/steps";
import { TabPanel, TabView } from "primereact/tabview";
import PaymentMethodPanel from "../components/PaymentMethodPanel";
import BillingAddressStripeElement from "../components/BillingAddressStripeElement";

export const BillingOneTimeCheckoutDialog = ({
  header,
  visible,
  onHide,
  details,
  renderCheckoutFormContent,
}: {
  header: string;
  visible: boolean;
  onHide: () => void;
  details: any;
  renderCheckoutFormContent: (submitLabel: string) => ReactNode;
}) => (
  <Dialog
    header={header}
    visible={visible}
    style={{ width: "92vw", maxWidth: "760px" }}
    breakpoints={{ "960px": "94vw", "640px": "98vw" }}
    onHide={onHide}
  >
    <div style={{ display: "grid", gap: "1rem" }}>
      <div
        style={{
          background: "linear-gradient(135deg, #fff7ed 0%, #ffffff 100%)",
          border: "1px solid #fed7aa",
          borderRadius: "16px",
          padding: "1rem 1.1rem",
        }}
      >
        <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.35rem" }}>
          {details?.title}
        </div>
        <div style={{ color: "#596F80", lineHeight: 1.6 }}>
          {details?.helperText}
        </div>
      </div>
      <div
        style={{
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: "16px",
          padding: "1rem 1.1rem",
        }}
      >
        <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.8rem" }}>Order Summary</div>
        <div style={{ display: "grid", gap: "0.7rem" }}>
          {details?.lineItems.map((lineItem: any) => (
            <div key={lineItem.label} className="flex justify-content-between align-items-center">
              <span style={{ color: "#596F80" }}>{lineItem.label}</span>
              <strong style={{ color: "#0f172a" }}>${lineItem.amount.toFixed(2)}</strong>
            </div>
          ))}
          <div className="flex justify-content-between align-items-center">
            <span style={{ color: "#596F80" }}>Tax</span>
            <strong style={{ color: "#0f172a" }}>${Number(details?.taxAmount || 0).toFixed(2)}</strong>
          </div>
          <div className="flex justify-content-between align-items-center" style={{ paddingTop: "0.35rem", borderTop: "1px solid #e2e8f0" }}>
            <span style={{ color: "#9a3412", fontWeight: 700 }}>Total</span>
            <strong style={{ color: "#9a3412", fontSize: "1.05rem" }}>${Number(details?.totalAmount || 0).toFixed(2)}</strong>
          </div>
        </div>
      </div>
      <div
        style={{
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: "16px",
          padding: "1rem 1.1rem",
        }}
      >
        <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.35rem" }}>Payment Method</div>
        <div style={{ color: "#596F80", lineHeight: 1.6, marginBottom: "0.9rem" }}>
          Add your card once to complete this purchase faster now and future automation credit purchases later.
        </div>
        {renderCheckoutFormContent(details?.submitLabel || "Pay Now")}
      </div>
    </div>
  </Dialog>
);

export const BillingPlanComparisonDialog = ({
  config,
  visible,
  onHide,
}: {
  config: any;
  visible: boolean;
  onHide: () => void;
}) => (
  <Dialog
    header={config.dialogHeader}
    visible={visible}
    style={{ width: "92vw", maxWidth: "820px" }}
    onHide={onHide}
  >
    <div style={{ display: "grid", gap: "1rem" }}>
      <div style={{ background: "linear-gradient(135deg, #eff6ff 0%, #ffffff 100%)", border: "1px solid #bfdbfe", borderRadius: "16px", padding: "1rem 1.1rem" }}>
        <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.35rem" }}>{config.heroTitle}</div>
        <div style={{ color: "#596F80", lineHeight: 1.7 }}>
          {config.heroDescription}
        </div>
      </div>
      <div className="grid">
        <div className="col-12 md:col-6">
          <div style={{ background: "linear-gradient(135deg, #fff7ed 0%, #ffffff 100%)", border: "1px solid #fdba74", borderRadius: "16px", padding: "1rem 1.1rem", height: "100%" }}>
            <div style={{ fontWeight: 700, color: "#9a3412" }}>{config.pricingTitle}</div>
            <div className="mt-2" style={{ fontSize: "1.9rem", fontWeight: 700, color: "#7c2d12" }}>{config.pricingAmount}</div>
            <div className="mt-2" style={{ color: "#596F80", lineHeight: 1.7 }}>
              {config.pricingDescription}
            </div>
            <ul style={{ marginTop: "0.9rem", paddingLeft: "1.1rem", lineHeight: 1.65, color: "#475569" }}>
              {config.pricingBullets.map((bullet: string) => (
                <li key={bullet}>{bullet}</li>
              ))}
            </ul>
          </div>
        </div>
        <div className="col-12 md:col-6">
          <div style={{ background: "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)", border: "1px solid #cbd5e1", borderRadius: "16px", padding: "1rem 1.1rem", height: "100%" }}>
            <div style={{ fontWeight: 700, color: "#0f172a" }}>{config.includedTitle}</div>
            <div className="mt-2" style={{ color: "#596F80", lineHeight: 1.7 }}>
              {config.includedDescription}
            </div>
            <div className="mt-3" style={{ border: "1px solid #e2e8f0", borderRadius: "14px", overflow: "hidden", background: "#ffffff" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f8fafc", color: "#334155" }}>
                    <th style={{ textAlign: "left", padding: "0.75rem 0.9rem", fontWeight: 700 }}>{config.includedColumns[0]}</th>
                    <th style={{ textAlign: "right", padding: "0.75rem 0.9rem", fontWeight: 700 }}>{config.includedColumns[1]}</th>
                  </tr>
                </thead>
                <tbody>
                  {config.includedRows.map((row: any) => (
                    <tr key={row.label} style={{ borderTop: "1px solid #e2e8f0" }}>
                      <td style={{ padding: "0.75rem 0.9rem", color: "#475569" }}>{row.label}</td>
                      <td style={{ padding: "0.75rem 0.9rem", color: "#0f172a", textAlign: "right" }}>{row.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div style={{ background: "linear-gradient(135deg, #ecfdf5 0%, #ffffff 100%)", border: "1px solid #86efac", borderRadius: "16px", padding: "1rem 1.1rem" }}>
        <div style={{ fontWeight: 700, color: "#166534" }}>{config.footerTitle}</div>
        <div className="mt-2" style={{ color: "#475569", lineHeight: 1.7 }}>
          {config.footerDescription}
        </div>
      </div>
    </div>
  </Dialog>
);

export const BillingPaymentHistoryContent = ({
  sectionCardStyle,
  cardTitleTemplate,
  invoiceHistory,
  getInvoiceStatusMeta,
  formatHistoryDate,
  openExternalUrl,
  nextInvoiceCursor,
  onLoadMoreInvoices,
  loadingMoreInvoices,
  featureCreditPurchaseHistory,
  getOneTimePurchaseStatusMeta,
  getFeatureCreditOption,
  nextFeatureCursor,
  onLoadMoreFeaturePurchases,
  loadingMoreFeatureHistory,
}: {
  sectionCardStyle: any;
  cardTitleTemplate: (title: string) => ReactNode;
  invoiceHistory: any[];
  getInvoiceStatusMeta: (status?: string | null) => any;
  formatHistoryDate: (value?: string | null) => string;
  openExternalUrl: (url?: string | null) => void;
  nextInvoiceCursor: string | null;
  onLoadMoreInvoices: () => Promise<void>;
  loadingMoreInvoices: boolean;
  featureCreditPurchaseHistory: any[];
  getOneTimePurchaseStatusMeta: () => any;
  getFeatureCreditOption: (featureType: string) => any;
  nextFeatureCursor: string | null;
  onLoadMoreFeaturePurchases: () => Promise<void>;
  loadingMoreFeatureHistory: boolean;
}) => (
  <div className="grid mt-2">
    <div className="col-12">
      <TabView className="billing-tabview">
        <TabPanel header="Subscription">
          <Card className="h-full mb-3" style={{ ...sectionCardStyle, padding: "0.5rem" }} header={cardTitleTemplate("Subscription Invoices")}>
            {invoiceHistory.length > 0 ? (
              <div className="flex flex-column gap-3">
                {invoiceHistory.map((invoice) => {
                  const invoiceStatusMeta = getInvoiceStatusMeta(invoice.status);
                  return (
                    <div key={invoice.invoiceId} style={{ background: invoiceStatusMeta.background, border: `1px solid ${invoiceStatusMeta.border}`, borderRadius: "12px", padding: "0.9rem 1rem" }}>
                      <div className="flex flex-wrap justify-content-between gap-3">
                        <div>
                          <div style={{ fontWeight: 700, color: invoiceStatusMeta.accent }}>
                            {invoice.description || "Subscription invoice"}
                          </div>
                          <div className="mt-1" style={{ color: invoiceStatusMeta.muted }}>
                            {formatHistoryDate(invoice.createdAt)} · {String(invoice.status || "").replace(/_/g, " ")}
                          </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontWeight: 700, color: invoiceStatusMeta.accent }}>${Number(invoice.amountPaid ?? invoice.amountDue ?? 0).toFixed(2)}</div>
                          <div className="mt-1" style={{ color: invoiceStatusMeta.muted }}>
                            {invoice.currency ? String(invoice.currency).toUpperCase() : ""}
                          </div>
                        </div>
                      </div>
                      {(invoice.hostedInvoiceUrl || invoice.invoicePdf || invoice.receiptUrl) && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          <Button
                            type="button"
                            label="Receipt Invoice"
                            severity="secondary"
                            text
                            rounded
                            onClick={() => openExternalUrl(invoice.receiptUrl || invoice.hostedInvoiceUrl || invoice.invoicePdf)}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
                {nextInvoiceCursor && (
                  <div className="pt-2">
                    <Button
                      type="button"
                      label="Load More Invoices"
                      severity="secondary"
                      outlined
                      rounded
                      onClick={onLoadMoreInvoices}
                      loading={loadingMoreInvoices}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div style={{ color: "#596F80", lineHeight: 1.7 }}>
                No subscription invoices have been recorded yet.
              </div>
            )}
          </Card>
        </TabPanel>
        <TabPanel header="Automation Credits">
          <Card className="h-full" style={{ ...sectionCardStyle, padding: "0.5rem" }} header={cardTitleTemplate("Automation Credits")}>
            {featureCreditPurchaseHistory.length > 0 ? (
              <div className="flex flex-column gap-3">
                {featureCreditPurchaseHistory.map((purchase) => {
                  const purchaseStatusMeta = getOneTimePurchaseStatusMeta();
                  return (
                    <div key={purchase.transactionId || purchase.sourceReferenceId} style={{ background: purchaseStatusMeta.background, border: `1px solid ${purchaseStatusMeta.border}`, borderRadius: "12px", padding: "0.9rem 1rem" }}>
                      <div className="flex flex-wrap justify-content-between gap-3">
                        <div>
                          <div style={{ fontWeight: 700, color: purchaseStatusMeta.accent }}>
                            {purchase.displayLabel || getFeatureCreditOption(purchase.featureType).label}
                          </div>
                          <div className="mt-1" style={{ color: purchaseStatusMeta.muted }}>
                            {formatHistoryDate(purchase.createdAt)} · {purchase.quantity} {purchase.quantityLabel || "bundle"}{purchase.quantity === 1 ? "" : "s"}
                          </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontWeight: 700, color: purchaseStatusMeta.accent }}>${Number(purchase.amount || 0).toFixed(2)}</div>
                          <div className="mt-1" style={{ color: purchaseStatusMeta.muted }}>
                            {purchase.mode === "saved_payment_method" ? "Saved card" : "Card payment"}
                          </div>
                        </div>
                      </div>
                      {(purchase.subtotalAmount != null || purchase.taxAmount != null) && (
                        <div className="mt-3" style={{ background: "#f8fafc", borderRadius: "12px", padding: "0.75rem 0.9rem", border: "1px solid #e2e8f0" }}>
                          <div className="flex justify-content-between align-items-center" style={{ color: "#596F80" }}>
                            <span>Subtotal</span>
                            <span style={{ fontWeight: 700, color: "#1f2937" }}>${Number(purchase.subtotalAmount || 0).toFixed(2)}</span>
                          </div>
                          {purchase.taxAmount != null && (
                            <div className="mt-2 flex justify-content-between align-items-center" style={{ color: "#596F80" }}>
                              <span>Tax</span>
                              <span style={{ fontWeight: 700, color: "#1f2937" }}>${Number(purchase.taxAmount || 0).toFixed(2)}</span>
                            </div>
                          )}
                          <div className="mt-2 flex justify-content-between align-items-center" style={{ color: "#9a3412" }}>
                            <span style={{ fontWeight: 700 }}>Total</span>
                            <span style={{ fontWeight: 700 }}>${Number(purchase.amount || 0).toFixed(2)}</span>
                          </div>
                        </div>
                      )}
                      {purchase.receiptUrl && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          <Button
                            type="button"
                            label="Receipt"
                            severity="secondary"
                            text
                            rounded
                            onClick={() => openExternalUrl(purchase.receiptUrl)}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
                {nextFeatureCursor && (
                  <div className="pt-2">
                    <Button
                      type="button"
                      label="Load More Purchases"
                      severity="secondary"
                      outlined
                      rounded
                      onClick={onLoadMoreFeaturePurchases}
                      loading={loadingMoreFeatureHistory}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div style={{ color: "#596F80", lineHeight: 1.7 }}>
                No bundle purchases have been recorded yet.
              </div>
            )}
          </Card>
        </TabPanel>
      </TabView>
    </div>
  </div>
);

export const BillingPaymentHistoryDialog = ({
  visible,
  onHide,
  sectionCardStyle,
  cardTitleTemplate,
  invoiceHistory,
  getInvoiceStatusMeta,
  formatHistoryDate,
  openExternalUrl,
  nextInvoiceCursor,
  onLoadMoreInvoices,
  loadingMoreInvoices,
  featureCreditPurchaseHistory,
  getOneTimePurchaseStatusMeta,
  getFeatureCreditOption,
  nextFeatureCursor,
  onLoadMoreFeaturePurchases,
  loadingMoreFeatureHistory,
}: {
  visible: boolean;
  onHide: () => void;
  sectionCardStyle: any;
  cardTitleTemplate: (title: string) => ReactNode;
  invoiceHistory: any[];
  getInvoiceStatusMeta: (status?: string | null) => any;
  formatHistoryDate: (value?: string | null) => string;
  openExternalUrl: (url?: string | null) => void;
  nextInvoiceCursor: string | null;
  onLoadMoreInvoices: () => Promise<void>;
  loadingMoreInvoices: boolean;
  featureCreditPurchaseHistory: any[];
  getOneTimePurchaseStatusMeta: () => any;
  getFeatureCreditOption: (featureType: string) => any;
  nextFeatureCursor: string | null;
  onLoadMoreFeaturePurchases: () => Promise<void>;
  loadingMoreFeatureHistory: boolean;
}) => (
  <Dialog
    header="Payment History"
    visible={visible}
    style={{ width: "92vw", maxWidth: "1100px" }}
    onHide={onHide}
  >
    <BillingPaymentHistoryContent
      sectionCardStyle={sectionCardStyle}
      cardTitleTemplate={cardTitleTemplate}
      invoiceHistory={invoiceHistory}
      getInvoiceStatusMeta={getInvoiceStatusMeta}
      formatHistoryDate={formatHistoryDate}
      openExternalUrl={openExternalUrl}
      nextInvoiceCursor={nextInvoiceCursor}
      onLoadMoreInvoices={onLoadMoreInvoices}
      loadingMoreInvoices={loadingMoreInvoices}
      featureCreditPurchaseHistory={featureCreditPurchaseHistory}
      getOneTimePurchaseStatusMeta={getOneTimePurchaseStatusMeta}
      getFeatureCreditOption={getFeatureCreditOption}
      nextFeatureCursor={nextFeatureCursor}
      onLoadMoreFeaturePurchases={onLoadMoreFeaturePurchases}
      loadingMoreFeatureHistory={loadingMoreFeatureHistory}
    />
  </Dialog>
);

export const BillingSubscriptionTabContent = ({
  isUserSubscribed,
  renderPaymentAttentionBanner,
  renderOutstandingPaymentsCard,
  subscriptionSummaryContent,
  visibleSubscribedSummaryServices,
  sectionCardStyle,
  cardTitleTemplate,
  renderCompactPlanRow,
  selectedSubscriptionServices,
  selectedSubscriptionServiceTypes,
  renderSubscriptionSignupCard,
  totalPrice,
  paymentIntentRef,
  existingPrice,
  cartLineItems,
  newItemsSubtotal,
  taxDetails,
  taxAmount,
  totalWithTax,
  isSubscriptionUpdatePreview,
  existingPriceLabel,
  updatedRecurringPrice,
  updatedRecurringPriceLabel,
  newItemsSubtotalLabel,
  totalPriceLabel,
  subscriptionCartHelperText,
  trialEndDate,
  subscriptionCancelDate,
  onProceedToPaymentSubmit,
  loadingPaymentIntent,
  paymentMethodSummary,
  renderPaymentSection,
  paymentMode,
  billingTabIndex,
  useExpandedPlanBuilder,
  renderPlanSectionWithoutCard,
  renderSummaryBesidePlan,
}: {
  isUserSubscribed: boolean;
  renderPaymentAttentionBanner: () => ReactNode;
  renderOutstandingPaymentsCard: () => ReactNode;
  subscriptionSummaryContent: ReactNode;
  visibleSubscribedSummaryServices: any[];
  sectionCardStyle: any;
  cardTitleTemplate: (title: string) => ReactNode;
  renderCompactPlanRow: (service: any, subscriptionType: string, idx: number) => ReactNode;
  selectedSubscriptionServices: any[];
  selectedSubscriptionServiceTypes: string[];
  renderSubscriptionSignupCard: (service: any, subscriptionType: string, idx: number) => ReactNode;
  totalPrice: number;
  paymentIntentRef: any;
  existingPrice: number;
  cartLineItems: any[];
  newItemsSubtotal: number;
  taxDetails: any;
  taxAmount: number;
  totalWithTax: number;
  isSubscriptionUpdatePreview: boolean;
  existingPriceLabel: string;
  updatedRecurringPrice: number;
  updatedRecurringPriceLabel: string;
  newItemsSubtotalLabel: string;
  totalPriceLabel: string;
  subscriptionCartHelperText: string;
  trialEndDate: Date | null;
  subscriptionCancelDate: Date | null;
  onProceedToPaymentSubmit: () => Promise<void>;
  loadingPaymentIntent: boolean;
  paymentMethodSummary: any;
  renderPaymentSection: (submitLabel: string) => ReactNode;
  paymentMode: string;
  billingTabIndex: number;
  useExpandedPlanBuilder?: boolean;
  renderPlanSectionWithoutCard?: boolean;
  renderSummaryBesidePlan?: boolean;
}) => {
  const shouldShowPlanSection = !isUserSubscribed || visibleSubscribedSummaryServices.length > 0 || selectedSubscriptionServices.length > 0;

  const planSectionContent = renderPlanSectionWithoutCard ? (
    <>
      {useExpandedPlanBuilder
        ? selectedSubscriptionServices.map((service: any, idx: number) => {
          const subscriptionType = selectedSubscriptionServiceTypes[idx];
          if (!subscriptionType) {
            return null;
          }

          return renderSubscriptionSignupCard(service, subscriptionType, idx);
        })
        : isUserSubscribed
          ? visibleSubscribedSummaryServices.length > 0 ? (
            <TabView className="billing-tabview">
              <TabPanel header="Plans">
                <Accordion multiple activeIndex={[]} className="mt-2">
                  {visibleSubscribedSummaryServices.map((item: any) => (
                    renderCompactPlanRow(item.service, item.subscriptionType, item.idx)
                  ))}
                </Accordion>
              </TabPanel>
            </TabView>
          ) : (
            selectedSubscriptionServices.map((service: any, idx: number) => {
              const subscriptionType = selectedSubscriptionServiceTypes[idx];
              if (!subscriptionType) {
                return null;
              }

              return renderSubscriptionSignupCard(service, subscriptionType, idx);
            })
          )
          : selectedSubscriptionServices.map((service: any, idx: number) => {
            const subscriptionType = selectedSubscriptionServiceTypes[idx];
            if (!subscriptionType) {
              return null;
            }

            return renderSubscriptionSignupCard(service, subscriptionType, idx);
          })}
    </>
  ) : (
    <Card className="h-full" style={sectionCardStyle} header={cardTitleTemplate("")}>
      {useExpandedPlanBuilder
        ? selectedSubscriptionServices.map((service: any, idx: number) => {
          const subscriptionType = selectedSubscriptionServiceTypes[idx];
          if (!subscriptionType) {
            return null;
          }

          return renderSubscriptionSignupCard(service, subscriptionType, idx);
        })
        : isUserSubscribed
          ? visibleSubscribedSummaryServices.length > 0 ? (
            <TabView className="billing-tabview">
              <TabPanel header="Plans">
                <Accordion multiple activeIndex={[]} className="mt-2">
                  {visibleSubscribedSummaryServices.map((item: any) => (
                    renderCompactPlanRow(item.service, item.subscriptionType, item.idx)
                  ))}
                </Accordion>
              </TabPanel>
            </TabView>
          ) : (
            selectedSubscriptionServices.map((service: any, idx: number) => {
              const subscriptionType = selectedSubscriptionServiceTypes[idx];
              if (!subscriptionType) {
                return null;
              }

              return renderSubscriptionSignupCard(service, subscriptionType, idx);
            })
          )
          : selectedSubscriptionServices.map((service: any, idx: number) => {
            const subscriptionType = selectedSubscriptionServiceTypes[idx];
            if (!subscriptionType) {
              return null;
            }

            return renderSubscriptionSignupCard(service, subscriptionType, idx);
          })}
    </Card>
  );

  const cartSectionContent = (
    <Card className="h-full" style={sectionCardStyle} header={cardTitleTemplate("Subscription Cart")}>
      <div className="justify-content-center align-items-center d-flex flex-wrap grid">
        <div className="col-12">
          {totalPrice > 0 ? (
            <div className="align-items-center justify-content-between gap-2">
              <div className="mb-3">
                <table className="w-full" style={{ borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#fff7ed", borderBottom: "1px solid #fed7aa" }}>
                      <th style={{ textAlign: "left", padding: "0.6rem" }}>Item</th>
                      <th style={{ textAlign: "right", padding: "0.6rem" }}>Qty</th>
                      <th style={{ textAlign: "right", padding: "0.6rem" }}>Line Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {existingPrice > 0 && (
                      <tr style={{ borderBottom: "1px solid #f3f4f6" }}>
                        <td style={{ padding: "0.6rem" }}><strong>{existingPriceLabel}</strong></td>
                        <td style={{ padding: "0.6rem", textAlign: "right" }}>-</td>
                        <td style={{ padding: "0.6rem", textAlign: "right" }}>${existingPrice.toFixed(2)}</td>
                      </tr>
                    )}
                    {isSubscriptionUpdatePreview && (
                      <tr style={{ borderBottom: "1px solid #f3f4f6", backgroundColor: "#f8fafc" }}>
                        <td style={{ padding: "0.6rem" }}><strong>{updatedRecurringPriceLabel}</strong></td>
                        <td style={{ padding: "0.6rem", textAlign: "right" }}>-</td>
                        <td style={{ padding: "0.6rem", textAlign: "right" }}>${updatedRecurringPrice.toFixed(2)}</td>
                      </tr>
                    )}
                    {cartLineItems.map((item: any) => (
                      <tr key={item.key} style={{ borderBottom: "1px solid #f3f4f6" }}>
                        <td style={{ padding: "0.6rem" }}>{item.name}</td>
                        <td style={{ padding: "0.6rem", textAlign: "right" }}>{item.quantity}</td>
                        <td style={{ padding: "0.6rem", textAlign: "right" }}>${item.lineTotal.toFixed(2)}</td>
                      </tr>
                    ))}
                    <tr style={{ borderTop: "2px solid #fdba74" }}>
                      <td style={{ padding: "0.6rem" }} colSpan={3}><strong>{newItemsSubtotalLabel}</strong></td>
                      <td style={{ padding: "0.6rem", textAlign: "right" }}><strong>${newItemsSubtotal.toFixed(2)}</strong></td>
                    </tr>
                    {taxDetails && (
                      <tr>
                        <td style={{ padding: "0.6rem" }} colSpan={3}>Tax</td>
                        <td style={{ padding: "0.6rem", textAlign: "right" }}>${taxAmount.toFixed(2)}</td>
                      </tr>
                    )}
                    <tr style={{ backgroundColor: "#ecfdf5", borderTop: "1px solid #86efac" }}>
                      <td style={{ padding: "0.6rem" }} colSpan={3}><strong>{totalPriceLabel || (taxDetails ? "Total (Incl. Tax)" : "Total Before Tax")}</strong></td>
                      <td style={{ padding: "0.6rem", textAlign: "right" }}><strong>${totalWithTax.toFixed(2)}</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div style={{ backgroundColor: "#f8fafc", borderRadius: "12px", padding: "0.9rem 1rem", border: "1px solid #e2e8f0", marginBottom: "1rem" }}>
                {trialEndDate && (
                  <div style={{ color: "green" }}>
                    <strong>Your free trial is active.</strong>
                    <div className="mt-2">Total Due now: $0</div>
                  </div>
                )}
                {subscriptionCancelDate && (
                  <div style={{ color: "#b91c1c" }}>
                    <strong>Your current subscription will be re-activated.</strong>
                  </div>
                )}
                {!trialEndDate && !subscriptionCancelDate && (
                  <div style={{ color: "#596F80" }}>
                    {subscriptionCartHelperText || (
                      taxDetails
                        ? "Review your updated total before continuing to payment."
                        : "Tax will be calculated after you continue to payment."
                    )}
                  </div>
                )}
              </div>
              <Button
                style={{ width: "100%" }}
                label={isUserSubscribed ? "Update Subscription" : "Proceed to Payment"}
                severity="warning"
                rounded
                onClick={onProceedToPaymentSubmit}
                loading={loadingPaymentIntent}
                data-telemetry-action={isUserSubscribed ? "billing_update_subscription" : "billing_proceed_to_payment"}
              />
            </div>
          ) : (
            <div
              style={{
                color: "#596F80",
                lineHeight: 1.7,
                background: "linear-gradient(135deg, #eff6ff 0%, #ffffff 100%)",
                border: "1px solid #bfdbfe",
                borderRadius: "14px",
                padding: "1rem",
              }}
            >
              Select one or more subscription plans to build your monthly cart. Your revised total will appear here instantly.
            </div>
          )}
        </div>
      </div>
    </Card>
  );

  return (
    <>
      {renderPaymentAttentionBanner()}
      {renderOutstandingPaymentsCard()}
      {renderSummaryBesidePlan ? (
        <div className="grid mt-3 align-items-stretch">
          {shouldShowPlanSection && (
            <div className="col-12 xl:col-7">
              <div className="h-full flex flex-column">
                {planSectionContent}
              </div>
            </div>
          )}
          {(subscriptionSummaryContent || totalPrice > 0) && (
            <div className="col-12 xl:col-5">
              <div className="h-full flex flex-column gap-3">
                {totalPrice > 0 && (
                  <div ref={paymentIntentRef} className="flex-1">
                    {cartSectionContent}
                  </div>
                )}
                {subscriptionSummaryContent && (
                  <div>
                    {subscriptionSummaryContent}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <>
          {subscriptionSummaryContent}
          <div className="grid mt-3">
            {shouldShowPlanSection && (
              <div className="col-12 lg:col-7">
                {planSectionContent}
              </div>
            )}
            {totalPrice > 0 && (
              <div ref={paymentIntentRef} className="col-12 lg:col-5">
                {cartSectionContent}
              </div>
            )}
          </div>
        </>
      )}
      {(paymentMode === "subscription" || (paymentMode === "cardUpdate" && billingTabIndex === 0)) &&
        renderPaymentSection(
          paymentMode === "cardUpdate"
            ? "Save Updated Card"
            : isUserSubscribed
              ? "Confirm Subscription Update"
              : "Start Subscription"
        )}
    </>
  );
};

export const BillingOneTimeTabContent = ({
  renderBundlePackages,
  paymentMode,
  billingTabIndex,
  renderPaymentSection,
  automationSummaryContent,
  automationCreditUsageContent,
}: {
  renderBundlePackages: () => ReactNode;
  paymentMode: string;
  billingTabIndex: number;
  renderPaymentSection: (submitLabel: string) => ReactNode;
  automationSummaryContent?: ReactNode;
  automationCreditUsageContent?: ReactNode;
}) => (
  <>
    <div
      className="mt-2 billing-automation-credits-layout"
      style={{
        display: "grid",
        gridTemplateColumns: `${automationSummaryContent ? "minmax(260px, 360px) " : ""}minmax(0, 1fr)${automationCreditUsageContent ? " minmax(260px, 340px)" : ""}`,
        gap: "1rem",
        alignItems: "start",
      }}
    >
      {automationSummaryContent && (
        <div>
          {automationSummaryContent}
        </div>
      )}
      <div>
        {renderBundlePackages()}
      </div>
      {automationCreditUsageContent ? (
        <div>
          {automationCreditUsageContent}
        </div>
      ) : null}
    </div>
    {(paymentMode === "cardUpdate" && billingTabIndex === 1) && renderPaymentSection("Save Payment Method")}
  </>
);

export const BillingPaymentDetailsDialog = ({
  visible,
  onHide,
  paymentMethodSummary,
  onUpdatePaymentMethod,
  loading,
}: {
  visible: boolean;
  onHide: () => void;
  paymentMethodSummary: any;
  onUpdatePaymentMethod: () => void;
  loading: boolean;
}) => (
  <Dialog
    header="Payment Details"
    visible={visible}
    style={{ width: "92vw", maxWidth: "720px" }}
    onHide={onHide}
  >
    <PaymentMethodPanel
      paymentMethodSummary={paymentMethodSummary}
      onUpdate={onUpdatePaymentMethod}
      loading={loading}
      title="Card on file"
      description="This saved card is used for subscription updates and can also be charged directly for automation credit purchases."
      emptyDescription="Add a customer payment method once so subscription updates and automation credit purchases can use the same saved card."
      showActionButton
    />
  </Dialog>
);

export const BillingAddressDialog = ({
  visible,
  billingAddressSaving,
  onHide,
  appDialogContentStyle,
  appDialogHeaderStyle,
  appDialogFooterStyle,
  paymentMethodSummary,
  wizardStep = 0,
  paymentContent,
  onWizardStepChange,
  onCancel,
  onSaveBillingAddress,
  onSaveBillingAddressAndUpdatePaymentMethod,
  showSaveBillingAddress = true,
  showPaymentMethodAction = true,
  getBillingFieldError,
  billingAddressDraft,
  onBillingAddressElementChange,
  hasCompleteBillingAddress,
}: {
  visible: boolean;
  billingAddressSaving: boolean;
  onHide: () => void;
  appDialogContentStyle: any;
  appDialogHeaderStyle: any;
  appDialogFooterStyle: any;
  paymentMethodSummary: any;
  wizardStep?: number;
  paymentContent?: ReactNode;
  onWizardStepChange?: (step: number) => void;
  showSaveBillingAddress?: boolean;
  showPaymentMethodAction?: boolean;
  onCancel: () => void;
  onSaveBillingAddress: () => Promise<void>;
  onSaveBillingAddressAndUpdatePaymentMethod: () => Promise<void>;
  getBillingFieldError: (field: string) => string;
  billingAddressDraft: any;
  onBillingAddressElementChange: (payload: { complete: boolean; address: any }) => void;
  hasCompleteBillingAddress: (address?: any) => boolean;
}) => (
  <Dialog
    header={showPaymentMethodAction ? "Subscription Setup" : "Billing Address"}
    visible={visible}
    style={{ width: "92vw", maxWidth: "640px" }}
    breakpoints={{ "960px": "94vw", "640px": "98vw" }}
    contentStyle={appDialogContentStyle}
    headerStyle={appDialogHeaderStyle}
    onHide={onHide}
    footer={(
      <div className="flex justify-content-end gap-2" style={appDialogFooterStyle}>
        <Button
          type="button"
          label="Cancel"
          severity="secondary"
          text
          rounded
          disabled={billingAddressSaving}
          onClick={onCancel}
        />
        {wizardStep > 0 && paymentContent ? (
          <Button
            type="button"
            label="Back to Address"
            severity="secondary"
            text
            rounded
            disabled={billingAddressSaving}
            onClick={() => onWizardStepChange?.(0)}
          />
        ) : null}
        {wizardStep === 0 && showSaveBillingAddress ? <Button
          type="button"
          label="Save Billing Address"
          severity="warning"
          rounded
          loading={billingAddressSaving}
          onClick={onSaveBillingAddress}
          data-telemetry-action="billing_save_address"
        /> : null}
        {wizardStep === 0 && showPaymentMethodAction ? <Button
          type="button"
          label={paymentMethodSummary ? "Save Address & Update Payment Method" : "Save Address & Add Payment Method"}
          severity="warning"
          rounded
          outlined
          loading={billingAddressSaving}
          onClick={onSaveBillingAddressAndUpdatePaymentMethod}
          data-telemetry-action={paymentMethodSummary ? "billing_save_address_and_update_payment_method" : "billing_save_address_and_add_payment_method"}
        /> : null}
      </div>
    )}
  >
    <div style={{ display: "grid", gap: "1rem" }}>
      {showPaymentMethodAction ? (
        <Steps
          model={[
            { label: "Add Address" },
            { label: "Add Payment Method" },
            { label: "Start Subscription" },
          ]}
          activeIndex={wizardStep}
          readOnly
          className="billing-subscription-wizard-steps"
        />
      ) : null}
      {wizardStep > 0 && paymentContent ? (
        <div>{paymentContent}</div>
      ) : <div>
        <div className="grid">
          {hasCompleteBillingAddress(billingAddressDraft) && (
            <div className="col-12">
              <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "14px", padding: "0.9rem 1rem" }}>
                <div style={{ fontSize: "0.82rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Selected Billing Address
                </div>
                <div className="mt-2" style={{ color: "#0f172a", lineHeight: 1.7 }}>
                  <div>{billingAddressDraft.line1}</div>
                  {billingAddressDraft.line2 && <div>{billingAddressDraft.line2}</div>}
                  <div>{billingAddressDraft.city}, {billingAddressDraft.state} {billingAddressDraft.postalCode}</div>
                  <div>{billingAddressDraft.country}</div>
                </div>
              </div>
            </div>
          )}
          <div className="col-12 field">
            <div
              style={{
                background: "#ffffff",
                border: `1px solid ${getBillingFieldError("addressElement") ? "#fca5a5" : "#dbe7f3"}`,
                borderRadius: "14px",
                padding: "0.85rem",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7)",
              }}
            >
              <BillingAddressStripeElement
                defaultAddress={billingAddressDraft}
                onAddressChange={onBillingAddressElementChange}
              />
            </div>
            {getBillingFieldError("addressElement") && <small className="p-error">{getBillingFieldError("addressElement")}</small>}
          </div>
        </div>
      </div>}
    </div>
  </Dialog>
);
